<template>
    <div class="match-function">
        <div
            v-for="item in matchFunctionItemsDisplay"
            v-bind:key="item.name"
            class="match-function-item"
            v-bind:class="[
                item.ga,
                item.isEnabled ? '' : '_disabled',
            ]"
            v-on:click="item.isEnabled && item.onClick()"
        >
            <Icons class="iconsSize-24" v-bind:iconName="item.icon" />
        </div>
        <div
            v-if="marketTypeCount > 0"
            class="match_closeMoreMarket"
            v-on:click="updateIsMoreMarketExpanded"
        >
            <div
                class="match_closeMoreMarket_btn"
                v-bind:class="{ JARVIS_MatchCard_Btn_MoreMarkets_Collapse: isMoreMarketExpanded }"
            >
                <span>{{ marketTypeCount }}</span>
                <SvgIcon v-if="isMoreMarketExpanded" iconName="systemIcon_minus" />
                <SvgIcon v-else iconName="systemIcon_plus" />
            </div>
        </div>
        <div v-else class="match_closeMoreMarket" />
    </div>
</template>

<script lang="ts">
    import { storeToRefs } from 'pinia';
    import type { PropType } from 'vue';
    import { computed, defineComponent, toRefs } from 'vue';
    import { useLiveStream } from '../liveStream/useLiveStream';
    import { openStatistics, useMatchFunction } from '@/components/matchFunction/useMatchFunction';
    import type { IMatch } from '@/interface/IMatch';
    import { MarketPage, MarketTab, MatchFunction } from '@/interface/enum';
    import { useApp } from '@/libraries/composables/useApp';
    import { addToCalendar } from '@/libraries/helpers/calendarHelper';
    import { calculateMoreMarketCount } from '@/libraries/oddsDisplay';
    import { useFeatureToggleStore } from '@/stores/featureToggleStore';
    import { useProductPlatformStore } from '@/stores/productPlatformStore';
    import { useRootStore } from '@/stores/rootStore';
    import Icons from '@/svg/Icons.vue';
    import SvgIcon from '@/svg/SvgIcon.vue';

    export default defineComponent({
        name: 'MatchFunction',
        components: {
            Icons,
            SvgIcon,
        },
        emits: ['updateIsMoreMarketExpanded'],
        props: {
            info: {
                type: Object as PropType<IMatch>,
                default: () => ({}),
            },
            selectedMarketTab: {
                type: Number,
                default: MarketTab.Sbo,
            },
            betBuilderMoreMarketCount: {
                type: Number,
                default: 0,
            },
            isMoreMarketExpanded: {
                type: Boolean,
                default: false,
            },
            marketCount: {
                type: Number,
                default: 0,
            },
        },
        setup(props, context) {
            const { isAsi, marketPage } = storeToRefs(useRootStore());
            const { isComponentInPp } = storeToRefs(useProductPlatformStore());
            const { isStatisticsEnabled } = storeToRefs(useFeatureToggleStore());

            const { info, selectedMarketTab, betBuilderMoreMarketCount, isMoreMarketExpanded, marketCount } = toRefs(props);
            const { goToMatchDetail } = useMatchFunction(info);

            const { isLiveStreamPlayable } = useLiveStream(info);

            const marketTypeCount = computed(() => {
                if (selectedMarketTab.value === MarketTab.Sbo) return calculateMoreMarketCount(info.value.sportType, info.value.isLive, info.value.marketTypeCount) + marketCount.value;
                if (selectedMarketTab.value === MarketTab.BetBuilder) return betBuilderMoreMarketCount.value;
                return 0;
            });

            const updateIsMoreMarketExpanded = () => {
                context.emit('updateIsMoreMarketExpanded', !isMoreMarketExpanded.value);
            };

            const isStatisticsBtnEnabled = computed(() => isStatisticsEnabled.value && info.value.hasStatistics);
            const isLiveCourtBtnEnabled = computed(() => info.value.liveCourtMatchId > 0);
            const isLiveStreamBtnEnabled = computed(() => isLiveStreamPlayable.value);
            const isOutright = computed(() => marketPage.value === MarketPage.Outright);
            const { isFromApp } = useApp();
            const matchFunctionItems = computed(() => [
                {
                    name: 'Statistic',
                    isShow: true,
                    isEnabled: isStatisticsBtnEnabled.value,
                    icon: 'systemIcon_chart',
                    ga: isStatisticsBtnEnabled.value
                        ? 'JARVIS_MatchCard_Btn_Statistics'
                        : 'JARVIS_MatchCard_Btn_Statistics_Disable',
                    onClick: () => openStatistics(info.value.eventId),
                },
                {
                    name: 'LiveVideo',
                    isShow: true,
                    isEnabled: isLiveStreamBtnEnabled.value,
                    icon: 'systemIcon_tvPlay',
                    ga: isLiveStreamBtnEnabled.value
                        ? 'JARVIS_MatchCard_Btn_LiveVideo'
                        : 'JARVIS_MatchCard_Btn_LiveVideo_Disable',
                    onClick: () => {
                        if (isLiveStreamPlayable.value) {
                            goToMatchDetail({ matchFunctionTab: MatchFunction.LiveStream });
                        }
                    },
                },
                {
                    name: 'LiveCourt',
                    isShow: true,
                    isEnabled: isLiveCourtBtnEnabled.value,
                    icon: 'systemIcon_courtFootball',
                    ga: isLiveCourtBtnEnabled.value
                        ? 'JARVIS_MatchCard_Btn_LiveCourt'
                        : '_disabled JARVIS_MatchCard_Btn_LiveCourt_Disable',
                    onClick: () => goToMatchDetail({ matchFunctionTab: MatchFunction.LiveCourt }),
                },
                {
                    name: 'Calender',
                    isShow: !info.value.isLive && !isOutright.value && !isFromApp,
                    isEnabled: true,
                    icon: 'systemIcon_addToCalendar',
                    ga: 'JARVIS_MatchCard_Btn_AddToCalendar',
                    onClick: () => addToCalendar(info.value),
                },
            ]);

            const matchFunctionItemsDisplay = computed(() => matchFunctionItems.value.filter(item => item.isShow));

            return {
                isAsi,
                marketPage,
                isComponentInPp,

                goToMatchDetail,
                matchFunctionItemsDisplay,

                marketTypeCount,
                isLiveStreamPlayable,
                updateIsMoreMarketExpanded,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @import "@/scss/components/matchFunction";
</style>
